import { useCallback, useRef } from 'react';
import type { MapMouseEvent } from 'mapbox-gl';

import type { GpAssignmentMapPoint } from '../../data-access/types/gp-sampling-assignment';

import type { AssignmentPointPopupData } from './assignment-point-popup';
import {
  ASSIGNMENT_POINTS_LAYER_ID,
  buildPointsLookup,
  resolveClickedPointId,
} from './assignment-points-layer';

export function useAssignmentPointClick(
  points: GpAssignmentMapPoint[],
  onSelect: (data: AssignmentPointPopupData | null) => void,
) {
  const pointsByIdRef = useRef<Map<number, GpAssignmentMapPoint>>(new Map());

  pointsByIdRef.current = buildPointsLookup(points);

  return useCallback(
    (event: MapMouseEvent) => {
      const features = event.target.queryRenderedFeatures(event.point, {
        layers: [ASSIGNMENT_POINTS_LAYER_ID],
      });

      if (!features.length) {
        onSelect(null);
        return;
      }

      const pointId = resolveClickedPointId(features[0]);
      if (pointId == null) {
        onSelect(null);
        return;
      }

      const point = pointsByIdRef.current.get(pointId);
      if (!point) {
        onSelect(null);
        return;
      }

      onSelect({
        point,
        longitude: event.lngLat.lng,
        latitude: event.lngLat.lat,
      });
    },
    [onSelect],
  );
}
